import { useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { StatistiquesFilters } from '@/hooks/useStatistiquesFilters';
import { useIndividualCampaigns } from '@/hooks/useIndividualCampaigns';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Fonction pour formater le délai intelligemment
const formatDelai = (delaiJours: number): string => {
  const totalSeconds = delaiJours * 24 * 60 * 60;
  
  if (totalSeconds < 60) {
    // Moins d'1 minute → afficher en secondes
    return `${Math.round(totalSeconds)}s`;
  } else if (delaiJours < 1/24) {
    // Moins d'1 heure → afficher en minutes
    const minutes = Math.round(delaiJours * 24 * 60);
    return `${minutes} min`;
  } else if (delaiJours < 1) {
    // Moins d'1 jour → afficher en heures
    const heures = (delaiJours * 24).toFixed(1);
    return `${heures}h`;
  } else {
    // 1 jour ou plus → afficher en jours
    return `${delaiJours} jour${delaiJours > 1 ? 's' : ''}`;
  }
};

// Tooltip personnalisé avec nom du candidat
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    
    // Filtrer pour éviter les doublons (Area + Line ont le même dataKey)
    const uniquePayload = payload.filter((entry: any, index: number, self: any[]) => 
      index === self.findIndex((e) => e.dataKey === entry.dataKey)
    );
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-sm mb-2">{data.candidate_name}</p>
        <p className="text-xs text-gray-500 mb-2">{data.formattedDate}</p>
        {uniquePayload.map((entry: any, index: number) => {
          let displayValue = '';
          let displayName = entry.name;
          
          // Afficher les vraies valeurs selon le type de métrique
          if (entry.dataKey === 'taux_participation') {
            displayValue = `${Math.round(entry.value)}%`;
            displayName = 'Taux de participation';
          } else if (entry.dataKey === 'delai_jours') {
            displayValue = formatDelai(entry.value);
            displayName = 'Délai';
          }
          
          return (
            <div key={index} className="flex items-center justify-between gap-8 text-xs">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: entry.color }} 
                />
                <span className="text-gray-600">{displayName}</span>
              </div>
              <span className="font-semibold">{displayValue}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

interface PerformanceChartProps {
  filters: StatistiquesFilters;
}

export const PerformanceChart = ({ filters }: PerformanceChartProps) => {
  const [visibleLines, setVisibleLines] = useState({
    tauxReponse: true,
    delaiTraitement: true // Activé par défaut maintenant
  });

  // Charger les campagnes individuelles terminées
  const { campaignsData, loading, error } = useIndividualCampaigns(filters);

  // Formater les données pour Recharts
  const chartData = Array.isArray(campaignsData) ? campaignsData.map(campaign => ({
    // Données originales pour le tooltip
    candidate_name: campaign.candidate_name,
    target_position: campaign.target_position,
    status: campaign.status,
    date: campaign.date,
    formattedDate: format(new Date(campaign.date_time), 'd MMM yyyy', { locale: fr }),
    is_refused: campaign.is_refused,
    // Données pour les axes
    taux_participation: campaign.taux_participation ?? 0, // Utiliser 0 si null
    delai_jours: campaign.delai_jours,
    // Timestamp pour le tri
    timestamp: new Date(campaign.date_time).getTime()
  })).sort((a, b) => a.timestamp - b.timestamp) : [];

  const toggleLine = (line: keyof typeof visibleLines) => {
    setVisibleLines(prev => ({
      ...prev,
      [line]: !prev[line]
    }));
  };

  // Gestion du loading
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Chargement du graphique...</div>
        </div>
      </div>
    );
  }

  // Gestion des erreurs
  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-red-500">Erreur: {error.message}</div>
        </div>
      </div>
    );
  }

  // Gestion du cas sans données
  if (!loading && chartData.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Évolution des performances
        </h3>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">
            Aucune donnée disponible pour la période sélectionnée
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      {/* Header avec titre et toggles */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          Évolution des performances
        </h3>
        
        {/* Toggles fonctionnels */}
        <div className="flex items-center gap-6">
          <button
            onClick={() => toggleLine('tauxReponse')}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <div className={`w-10 h-5 rounded-full transition-all duration-200 ${
              visibleLines.tauxReponse ? 'toggle-active' : 'bg-gray-300'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${
                visibleLines.tauxReponse ? 'translate-x-5' : 'translate-x-0.5'
              } mt-0.5`} />
            </div>
            <span className={`text-sm font-medium transition-colors duration-150 ${
              visibleLines.tauxReponse ? 'text-gray-900' : 'text-gray-400'
            }`}>
              Taux de participation
            </span>
          </button>

          <button
            onClick={() => toggleLine('delaiTraitement')}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <div className={`w-10 h-5 rounded-full transition-all duration-200 ${
              visibleLines.delaiTraitement ? 'toggle-active' : 'bg-gray-300'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${
                visibleLines.delaiTraitement ? 'translate-x-5' : 'translate-x-0.5'
              } mt-0.5`} />
            </div>
            <span className={`text-sm font-medium transition-colors duration-150 ${
              visibleLines.delaiTraitement ? 'text-gray-900' : 'text-gray-400'
            }`}>
              Délai moyen
            </span>
          </button>
        </div>
      </div>

      {/* Graphique */}
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              {/* Gradient pour la zone cyan (Taux de réponse) */}
              <linearGradient id="colorTaux" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
              </linearGradient>
              {/* Gradient pour la zone verte (Délai) */}
              <linearGradient id="colorDelai" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#E5E7EB" 
              vertical={false} 
            />
            
            <XAxis
              dataKey="formattedDate"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            
            {/* Axe Y gauche : Taux de participation (0-100%) */}
            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(value) => `${value}%`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              label={{ 
                value: 'Taux de participation (%)', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6B7280', fontSize: 11 }
              }}
            />
            
            {/* Axe Y droit : Délai en jours (0-30) */}
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 30]}
              ticks={[0, 5, 10, 15, 20, 25, 30]}
              tickFormatter={(value) => `${value}j`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              label={{ 
                value: 'Délai (jours)', 
                angle: 90, 
                position: 'insideRight',
                style: { fill: '#6B7280', fontSize: 11 }
              }}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* Zone et ligne pour le taux de participation (cyan) */}
            {visibleLines.tauxReponse && (
              <>
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="taux_participation"
                  fill="url(#colorTaux)"
                  stroke="none"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="taux_participation"
                  stroke="#06B6D4"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#06B6D4', strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  name="Taux de participation"
                />
              </>
            )}
            
            {/* Zone et ligne pour le délai (vert) */}
            {visibleLines.delaiTraitement && (
              <>
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="delai_jours"
                  fill="url(#colorDelai)"
                  stroke="none"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="delai_jours"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#10B981', strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  name="Délai"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};